/**
 * PrimePrint Supply — theme.js
 * Vanilla JS IIFE. Extends window.PPTheme (routes/strings set in theme.liquid).
 */
(function () {
  'use strict';

  var PP = window.PPTheme || {};
  PP.routes = PP.routes || {};
  PP.strings = PP.strings || {};

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var recentKey = 'pp_recently_viewed';
  var recentSearchKey = 'pp_recent_searches';
  var recentMax = 12;
  var recentSearchMax = 6;
  var searchDebounceMs = 280;
  var searchTimer = null;
  var lastFocus = null;

  /* ── Utilities ── */
  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function on(el, evt, fn) {
    if (!el) return;
    el.addEventListener(evt, fn);
  }

  function delegate(root, evt, sel, fn) {
    if (!root) return;
    root.addEventListener(evt, function (e) {
      var t = e.target.closest(sel);
      if (t && root.contains(t)) fn.call(t, e, t);
    });
  }

  function fetchJSON(url, opts) {
    return fetch(url, opts).then(function (res) {
      if (!res.ok) throw new Error('Request failed');
      return res.json();
    });
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function trapFocus(container) {
    var nodes = qsa(FOCUSABLE, container).filter(function (n) {
      return n.offsetParent !== null || n === document.activeElement;
    });
    if (!nodes.length) return function () {};
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handler);
    return function () {
      container.removeEventListener('keydown', handler);
    };
  }

  function lockScroll() {
    document.body.classList.add('pp-scroll-lock');
  }

  function unlockScroll() {
    document.body.classList.remove('pp-scroll-lock');
  }

  function sectionHTML(url, sectionId) {
    return fetch(url + '?sections=' + encodeURIComponent(sectionId))
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        return data[sectionId] || '';
      });
  }

  function updateCartCount(count) {
    qsa('[data-pp-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.setAttribute('data-count', String(count));
      el.setAttribute('aria-label', count + ' items in cart');
    });
  }

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {}, bubbles: true }));
  }

  /* ── Mega menu (desktop) ── */
  function initMegaMenu() {
    var toggle = qs('[data-pp-mega-toggle]');
    var panel = qs('[data-pp-mega]');
    if (!toggle || !panel) return;

    var releaseTrap = null;

    function close() {
      toggle.setAttribute('aria-expanded', 'false');
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      if (releaseTrap) {
        releaseTrap();
        releaseTrap = null;
      }
    }

    function open() {
      toggle.setAttribute('aria-expanded', 'true');
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      releaseTrap = trapFocus(panel);
    }

    on(toggle, 'click', function (e) {
      e.preventDefault();
      if (panel.classList.contains('is-open')) close();
      else open();
    });

    on(document, 'click', function (e) {
      if (!panel.classList.contains('is-open')) return;
      if (toggle.contains(e.target) || panel.contains(e.target)) return;
      close();
    });

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) {
        close();
        toggle.focus();
      }
    });
  }

  /* ── Mobile drawer + accordions ── */
  function initMobileNav() {
    var openBtn = qs('[data-pp-mobile-open]');
    var drawer = qs('[data-pp-mobile-drawer]');
    var overlay = qs('[data-pp-mobile-overlay]');
    if (!drawer) return;

    var releaseTrap = null;

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.classList.remove('is-visible');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      unlockScroll();
      if (releaseTrap) {
        releaseTrap();
        releaseTrap = null;
      }
      if (lastFocus) lastFocus.focus();
    }

    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('is-visible');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      lockScroll();
      releaseTrap = trapFocus(drawer);
      var first = qs(FOCUSABLE, drawer);
      if (first) first.focus();
    }

    if (openBtn) {
      on(openBtn, 'click', function (e) {
        e.preventDefault();
        if (drawer.classList.contains('is-open')) close();
        else open();
      });
    }

    qsa('[data-pp-mobile-close]', drawer).concat(overlay ? [overlay] : []).forEach(function (el) {
      on(el, 'click', function (e) {
        e.preventDefault();
        close();
      });
    });

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    qsa('[data-pp-accordion-trigger]', drawer).forEach(function (btn) {
      var panelId = btn.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : btn.nextElementSibling;
      if (!panel) return;

      on(btn, 'click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        panel.classList.toggle('is-open', !expanded);
        panel.hidden = expanded;
      });
    });
  }

  /* ── Fixed header height + utility dismiss ── */
  function initFixedHeader() {
    var shell = qs('[data-pp-header-fixed]');
    if (!shell) return;

    function updateOffset() {
      var height = shell.offsetHeight;
      document.documentElement.style.setProperty('--pp-header-offset', height + 'px');
    }

    on(window, 'resize', updateOffset);

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(updateOffset);
      ro.observe(shell);
    }

    updateOffset();
    return updateOffset;
  }

  function initUtilityDismiss(updateHeaderOffset) {
    var utility = qs('[data-pp-utility]');
    var dismissBtn = qs('[data-pp-utility-dismiss]');
    if (!utility || !dismissBtn) return;

    var storageKey = 'pp_utility_dismissed';

    try {
      if (localStorage.getItem(storageKey) === '1') {
        utility.classList.add('is-dismissed');
        if (typeof updateHeaderOffset === 'function') updateHeaderOffset();
      }
    } catch (e) {
      /* ignore */
    }

    on(dismissBtn, 'click', function () {
      utility.classList.add('is-dismissed');
      try {
        localStorage.setItem(storageKey, '1');
      } catch (e) {
        /* ignore */
      }
      if (typeof updateHeaderOffset === 'function') updateHeaderOffset();
    });
  }

  function getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem(recentSearchKey) || '[]');
    } catch (e) {
      return [];
    }
  }

  function saveRecentSearch(term) {
    if (!term || term.length < 2) return;
    var list = getRecentSearches().filter(function (item) {
      return item !== term;
    });
    list.unshift(term);
    list = list.slice(0, recentSearchMax);
    try {
      localStorage.setItem(recentSearchKey, JSON.stringify(list));
    } catch (e) {
      /* ignore */
    }
  }

  /* ── Predictive search ── */
  function initPredictiveSearch(root) {
    var scope = root || document;
    qsa('[data-pp-search]', scope).forEach(function (wrap) {
      if (wrap.dataset.ppSearchInit) return;
      wrap.dataset.ppSearchInit = '1';

      var form = qs('form', wrap) || wrap;
      var input = qs('[data-pp-search-input]', wrap) || qs('input[type="search"], input[type="text"]', wrap);
      var results = qs('[data-pp-predictive]', wrap);
      var overlay = qs('[data-pp-search-overlay]');
      var overlayInput = overlay ? qs('[data-pp-search-input]', overlay) : null;
      var activeIndex = -1;

      if (!input || !PP.routes.predictive_search_url) return;

      function renderSuggestions() {
        if (!results) return;
        var recent = getRecentSearches();
        var chips = ['Ink & Toner', 'Copy Paper', 'Shipping Boxes', 'Office Supplies', 'Printer'];
        var html = '';

        if (recent.length) {
          html += '<div class="pp-predictive__recent"><div class="pp-predictive__recent-label">Recent searches</div></div><div class="pp-predictive__chips">';
          recent.forEach(function (term) {
            html +=
              '<a class="pp-predictive__chip" href="/search?q=' +
              encodeURIComponent(term) +
              '">' +
              term +
              '</a>';
          });
          html += '</div>';
        }

        html += '<div class="pp-predictive__recent"><div class="pp-predictive__recent-label">Popular categories</div></div><div class="pp-predictive__chips">';
        chips.forEach(function (term) {
          html +=
            '<a class="pp-predictive__chip" href="/search?q=' +
            encodeURIComponent(term) +
            '">' +
            term +
            '</a>';
        });
        html += '</div>';

        results.innerHTML = html;
        results.classList.add('is-open');
        results.setAttribute('aria-hidden', 'false');
      }

      function renderEmpty(msg) {
        if (!results) return;
        results.innerHTML = '<div class="pp-predictive__status" role="status">' + msg + '</div>';
        results.classList.add('is-open');
        results.setAttribute('aria-hidden', 'false');
      }

      function renderResults(data) {
        if (!results) return;
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (!products.length) {
          renderEmpty(PP.strings.searchNoResults || 'No results found');
          return;
        }

        var html = '<div class="pp-predictive__list" role="listbox">';
        products.forEach(function (p, i) {
          var img = p.featured_image ? p.featured_image.url : '';
          var price = p.price ? formatMoney(p.price) : '';
          html +=
            '<a class="pp-predictive__item" role="option" data-index="' +
            i +
            '" href="' +
            p.url +
            '">' +
            (img ? '<img src="' + img + '" alt="" width="56" height="56" loading="lazy">' : '<span></span>') +
            '<div><div class="pp-predictive__title">' +
            p.title +
            '</div>' +
            '</div>' +
            (price ? '<div class="pp-predictive__price">' + price + '</div>' : '') +
            '</a>';
        });
        html += '</div>';
        html +=
          '<div class="pp-predictive__footer"><a class="pp-predictive__view-all" href="/search?q=' +
          encodeURIComponent(input.value.trim()) +
          '">View all results</a></div>';
        results.innerHTML = html;
        results.classList.add('is-open');
        results.setAttribute('aria-hidden', 'false');
        activeIndex = -1;
      }

      function search(q) {
        if (!q || q.length < 2) {
          renderSuggestions();
          return;
        }
        var url =
          PP.routes.predictive_search_url +
          '?q=' +
          encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=6&section_id=predictive-search';
        fetch(url, { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.json();
          })
          .then(renderResults)
          .catch(function () {
            renderEmpty(PP.strings.searchNoResults || 'No results found');
          });
      }

      function debouncedSearch() {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () {
          search(input.value.trim());
        }, searchDebounceMs);
      }

      on(input, 'input', debouncedSearch);
      on(input, 'focus', function () {
        if (!input.value.trim()) renderSuggestions();
        else debouncedSearch();
      });

      on(form, 'submit', function () {
        saveRecentSearch(input.value.trim());
      });

      on(input, 'keydown', function (e) {
        if (!results || !results.classList.contains('is-open')) return;
        var items = qsa('.pp-predictive__item', results);
        if (!items.length) return;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          activeIndex = Math.min(activeIndex + 1, items.length - 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          activeIndex = Math.max(activeIndex - 1, 0);
        } else if (e.key === 'Enter' && activeIndex >= 0) {
          e.preventDefault();
          window.location.href = items[activeIndex].href;
          return;
        } else if (e.key === 'Escape') {
          results.classList.remove('is-open');
          return;
        } else {
          return;
        }
        items.forEach(function (el, i) {
          el.classList.toggle('is-active', i === activeIndex);
        });
        if (activeIndex >= 0) items[activeIndex].focus();
      });

      on(document, 'click', function (e) {
        if (!wrap.contains(e.target)) {
          if (results) {
            results.classList.remove('is-open');
            results.setAttribute('aria-hidden', 'true');
          }
        }
      });

      on(form, 'submit', function (e) {
        if (!input.value.trim()) e.preventDefault();
      });

      var openOverlayBtn = qs('[data-pp-search-open]', scope);
      if (overlay && openOverlayBtn) {
        on(openOverlayBtn, 'click', function (e) {
          e.preventDefault();
          overlay.classList.add('is-open');
          overlay.setAttribute('aria-hidden', 'false');
          lockScroll();
          if (overlayInput) overlayInput.focus();
        });
        qsa('[data-pp-search-close]', overlay).forEach(function (btn) {
          on(btn, 'click', function (e) {
            e.preventDefault();
            overlay.classList.remove('is-open');
            overlay.setAttribute('aria-hidden', 'true');
            unlockScroll();
          });
        });
        if (overlayInput) initPredictiveSearch(overlay);
      }
    });
  }

  /* ── Cart drawer ── */
  var Cart = {
    drawer: null,
    overlay: null,
    releaseTrap: null,
    sectionId: 'cart-drawer',
    globalBound: false,

    init: function () {
      this.drawer = qs('[data-pp-cart-drawer]');
      this.overlay = qs('[data-pp-cart-overlay]');
      if (!this.drawer) return;

      var self = this;

      if (!this.globalBound) {
        this.globalBound = true;

        delegate(document, 'click', '[data-pp-cart-open]', function (e) {
          e.preventDefault();
          self.open();
        });

        delegate(document, 'click', '[data-pp-cart-close]', function (e) {
          e.preventDefault();
          self.close();
        });

        if (this.overlay) {
          on(this.overlay, 'click', function (e) {
            e.preventDefault();
            self.close();
          });
        }

        on(document, 'keydown', function (e) {
          if (e.key === 'Escape' && self.drawer && self.drawer.classList.contains('is-open')) self.close();
        });
      }

      if (!this.drawer.dataset.ppCartRecBound) {
        this.drawer.dataset.ppCartRecBound = 'true';
        delegate(this.drawer, 'click', '[data-pp-cart-rec-add]', function (e, btn) {
          e.preventDefault();
          var id = btn.getAttribute('data-variant-id');
          if (id) self.add(id, 1);
        });
      }
    },

    open: function () {
      if (!this.drawer) return;
      lastFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('is-visible');
      lockScroll();
      this.releaseTrap = trapFocus(this.drawer);
      var closeBtn = qs('[data-pp-cart-close]', this.drawer);
      if (closeBtn) closeBtn.focus();
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('is-visible');
      unlockScroll();
      if (this.releaseTrap) {
        this.releaseTrap();
        this.releaseTrap = null;
      }
      if (lastFocus) lastFocus.focus();
    },

    refresh: function () {
      var self = this;
      if (!this.drawer) return Promise.resolve();
      var url = (PP.routes.cart_url || '/cart') + '?section_id=' + this.sectionId;
      return sectionHTML(PP.routes.cart_url || '/cart', this.sectionId)
        .then(function (html) {
          if (!html) return;
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var fresh = qs('[data-pp-cart-drawer]', doc);
          if (fresh) self.drawer.innerHTML = fresh.innerHTML;
          self.init();
        })
        .catch(function () {});
    },

    add: function (variantId, quantity, properties) {
      var self = this;
      var body = {
        items: [
          {
            id: Number(variantId),
            quantity: quantity || 1,
            properties: properties || {},
          },
        ],
      };

      return fetchJSON((PP.routes.cart_add_url || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (item) {
          return fetchJSON((PP.routes.cart_url || '/cart') + '.js').then(function (cart) {
            updateCartCount(cart.item_count);
            return self.refresh().then(function () {
              self.open();
              dispatch('pp:cart:added', { item: item, cart: cart });
              return cart;
            });
          });
        })
        .catch(function (err) {
          alert(PP.strings.cartError || 'Could not update cart. Please try again.');
          throw err;
        });
    },

    change: function (line, quantity) {
      var self = this;
      return fetchJSON((PP.routes.cart_change_url || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity }),
      })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          dispatch('pp:cart:updated', { cart: cart });
          return self.refresh().then(function () {
            return cart;
          });
        })
        .catch(function (err) {
          alert(PP.strings.cartError || 'Could not update cart. Please try again.');
          throw err;
        });
    },

    updateSections: function () {
      var main = qs('[data-pp-cart-main]');
      if (!main) return Promise.resolve();
      return sectionHTML(PP.routes.cart_url || '/cart', 'main-cart').then(function (html) {
        if (html) main.innerHTML = html;
        initQuantitySteppers(main);
        initCartLineItems(main);
      });
    },
  };

  function initAddToCart(root) {
    delegate(root || document, 'click', '[data-pp-add-to-cart]', function (e, btn) {
      e.preventDefault();
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      var form = btn.closest('form');
      var variantId = btn.getAttribute('data-variant-id');
      var qty = 1;

      if (form) {
        var idInput = qs('[name="id"]', form);
        if (idInput) variantId = idInput.value;
        var qtyInput = qs('[name="quantity"], [data-pp-qty-input]', form);
        if (qtyInput) qty = parseInt(qtyInput.value, 10) || 1;
      }

      if (!variantId) return;
      var keepDisabled = btn.disabled;
      btn.classList.add('pp-loading');
      btn.disabled = true;
      Cart.add(variantId, qty)
        .finally(function () {
          btn.classList.remove('pp-loading');
          btn.disabled = keepDisabled;
        });
    });

    delegate(root || document, 'submit', 'form[data-pp-product-form]', function (e, form) {
      if (form.dataset.ppAjax === 'false') return;
      e.preventDefault();
      var btn = qs('[data-pp-add-to-cart]', form) || qs('[type="submit"]', form);
      if (btn) btn.click();
    });
  }

  function initCartLineItems(root) {
    var scope = root || document;

    delegate(scope, 'click', '[data-pp-line-remove]', function (e, btn) {
      e.preventDefault();
      var line = parseInt(btn.getAttribute('data-line'), 10);
      if (!line) return;
      Cart.change(line, 0).then(function () {
        return Cart.updateSections();
      });
    });

    delegate(scope, 'change', '[data-pp-line-qty]', function (e, input) {
      var line = parseInt(input.getAttribute('data-line'), 10);
      var qty = parseInt(input.value, 10);
      if (!line || isNaN(qty)) return;
      Cart.change(line, qty).then(function () {
        return Cart.updateSections();
      });
    });
  }

  /* ── Quantity steppers ── */
  function initQuantitySteppers(root) {
    var scope = root || document;

    delegate(scope, 'click', '[data-qty-minus]', function (e, btn) {
      e.preventDefault();
      var wrap = btn.closest('[data-pp-qty]') || btn.parentElement;
      var input = qs('[data-pp-qty-input]', wrap) || qs('input', wrap);
      var display = qs('[data-pp-qty-value]', wrap);
      var min = parseInt(btn.getAttribute('data-min') || '1', 10);
      var val = input ? parseInt(input.value, 10) : parseInt(display && display.textContent, 10);
      val = Math.max(min, (val || 1) - 1);
      if (input) {
        input.value = val;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (display) display.textContent = val;
    });

    delegate(scope, 'click', '[data-qty-plus]', function (e, btn) {
      e.preventDefault();
      var wrap = btn.closest('[data-pp-qty]') || btn.parentElement;
      var input = qs('[data-pp-qty-input]', wrap) || qs('input', wrap);
      var display = qs('[data-pp-qty-value]', wrap);
      var max = btn.getAttribute('data-max');
      var val = input ? parseInt(input.value, 10) : parseInt(display && display.textContent, 10);
      val = (val || 1) + 1;
      if (max) val = Math.min(parseInt(max, 10), val);
      if (input) {
        input.value = val;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (display) display.textContent = val;
    });
  }

  /* ── Product gallery ── */
  function initProductGallery(root) {
    qsa('[data-pp-gallery]', root || document).forEach(function (gallery) {
      if (gallery.dataset.ppGalleryInit) return;
      gallery.dataset.ppGalleryInit = '1';

      var main = qs('[data-pp-gallery-main]', gallery);
      var mainImg = main ? qs('img', main) : null;
      if (!mainImg) return;

      qsa('[data-pp-gallery-thumb]', gallery).forEach(function (thumb, i) {
        on(thumb, 'click', function () {
          qsa('[data-pp-gallery-thumb]', gallery).forEach(function (t) {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });
          thumb.classList.add('is-active');
          thumb.setAttribute('aria-selected', 'true');
          var src = thumb.getAttribute('data-src') || (qs('img', thumb) && qs('img', thumb).src);
          var alt = thumb.getAttribute('data-alt') || (qs('img', thumb) && qs('img', thumb).alt) || '';
          if (src) {
            mainImg.src = src;
            mainImg.alt = alt;
          }
        });
        if (i === 0) {
          thumb.classList.add('is-active');
          thumb.setAttribute('aria-selected', 'true');
        }
      });
    });
  }

  /* ── Product tabs ── */
  function initProductTabs(root) {
    qsa('[data-pp-tabs]', root || document).forEach(function (tabs) {
      if (tabs.dataset.ppTabsInit) return;
      tabs.dataset.ppTabsInit = '1';

      var buttons = qsa('[data-pp-tab]', tabs);
      var panels = qsa('[data-pp-tab-panel]', tabs);

      function activate(id) {
        buttons.forEach(function (btn) {
          var match = btn.getAttribute('data-pp-tab') === id;
          btn.classList.toggle('is-active', match);
          btn.setAttribute('aria-selected', match ? 'true' : 'false');
        });
        panels.forEach(function (panel) {
          var match = panel.getAttribute('data-pp-tab-panel') === id;
          panel.classList.toggle('is-active', match);
          panel.hidden = !match;
          panel.setAttribute('aria-hidden', match ? 'false' : 'true');
        });
      }

      buttons.forEach(function (btn) {
        on(btn, 'click', function () {
          activate(btn.getAttribute('data-pp-tab'));
        });
      });

      if (buttons[0]) activate(buttons[0].getAttribute('data-pp-tab'));
    });
  }

  /* ── Sticky ATC ── */
  function initStickyATC() {
    var bar = qs('[data-pp-sticky-atc]');
    var target = qs('[data-pp-product-atc]');
    if (!bar || !target || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var showBar = !entry.isIntersecting;
          bar.classList.toggle('is-visible', showBar);
          bar.setAttribute('aria-hidden', showBar ? 'false' : 'true');
          document.body.classList.toggle('pp-sticky-atc-active', showBar);
        });
      },
      { root: null, threshold: 0 }
    );
    observer.observe(target);

    on(qs('[data-pp-sticky-atc-btn]', bar), 'click', function (e) {
      e.preventDefault();
      var btn = qs('[data-pp-add-to-cart]', target);
      if (btn && !btn.disabled) btn.click();
    });
  }

  /* ── Recently viewed ── */
  function getRecent() {
    try {
      return JSON.parse(localStorage.getItem(recentKey) || '[]');
    } catch (e) {
      return [];
    }
  }

  function saveRecent(list) {
    try {
      localStorage.setItem(recentKey, JSON.stringify(list.slice(0, recentMax)));
    } catch (e) {}
  }

  function trackProductView() {
    var data = qs('[data-pp-product-json]');
    if (!data) return;
    try {
      var product = JSON.parse(data.textContent);
      if (!product || !product.id) return;
      var list = getRecent().filter(function (p) {
        return p.id !== product.id;
      });
      list.unshift({
        id: product.id,
        handle: product.handle,
        title: product.title,
        url: product.url,
        featured_image: product.featured_image,
        vendor: product.vendor,
        price: product.price,
      });
      saveRecent(list);
    } catch (e) {}
  }

  function renderRecentlyViewed() {
    var section = qs('[data-pp-recently-viewed]');
    if (!section) return;

    var grid = qs('[data-pp-recently-grid]', section) || section;
    var currentId = section.getAttribute('data-current-product-id');
    var handles = (section.getAttribute('data-product-handles') || '')
      .split(',')
      .filter(Boolean);

    var stored = getRecent().filter(function (p) {
      return String(p.id) !== String(currentId);
    });

    if (!stored.length) {
      section.classList.add('is-empty');
      return;
    }

    section.classList.remove('is-empty');

    if (handles.length) {
      return;
    }

    var html = '';
    stored.slice(0, 5).forEach(function (p) {
      html +=
        '<div class="pp-prod">' +
        '<a class="pp-prod__media" href="' +
        p.url +
        '">' +
        (p.featured_image
          ? '<img src="' + p.featured_image + '" alt="' + (p.title || '').replace(/"/g, '&quot;') + '" loading="lazy">'
          : '') +
        '</a><div class="pp-prod__body">' +
        '<div class="pp-prod__title"><a href="' +
        p.url +
        '">' +
        p.title +
        '</a></div>' +
        (p.price ? '<div class="pp-prod__price-row"><span class="pp-prod__price">' + formatMoney(p.price) + '</span></div>' : '') +
        '</div></div>';
    });
    grid.innerHTML = html;
  }

  /* ── Collection sort ── */
  function initCollectionSort() {
    qsa('[data-pp-sort]').forEach(function (select) {
      on(select, 'change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.href = url.toString();
      });
    });
  }

  /* ── Filter drawer (mobile) ── */
  function initFilterDrawer() {
    var toggle = qs('[data-pp-filter-toggle]');
    var filters = qs('[data-pp-filters]');
    if (!toggle || !filters) return;

    on(toggle, 'click', function () {
      var open = filters.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) lockScroll();
      else unlockScroll();
    });
  }

  /* ── LeadConnector live chat (contact page) ── */
  function initLiveChat() {
    var buttons = qsa('[data-pp-live-chat-open]');
    if (!buttons.length) return;

    function openWidget() {
      if (
        window.leadConnector &&
        window.leadConnector.chatWidget &&
        typeof window.leadConnector.chatWidget.openWidget === 'function'
      ) {
        window.leadConnector.chatWidget.openWidget();
        return true;
      }
      return false;
    }

    function openChat() {
      document.body.classList.add('pp-live-chat-open');
      if (!openWidget()) {
        window.addEventListener(
          'LC_chatWidgetLoaded',
          function () {
            openWidget();
          },
          { once: true }
        );
      }
    }

    buttons.forEach(function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        openChat();
      });
    });

    window.addEventListener(
      'LC_chatWidgetLoaded',
      function () {
        document.body.classList.add('pp-lc-chat-loaded');
      },
      false
    );
  }

  /* ── Master init ── */
  function init(root) {
    var updateHeaderOffset = initFixedHeader();
    initUtilityDismiss(updateHeaderOffset);
    initMegaMenu();
    initMobileNav();
    initPredictiveSearch(root);
    Cart.init();
    initAddToCart(root);
    initCartLineItems(root);
    initQuantitySteppers(root);
    initProductGallery(root);
    initProductTabs(root);
    initStickyATC();
    initCollectionSort();
    initFilterDrawer();
    initLiveChat();
    renderRecentlyViewed();
  }

  document.addEventListener('DOMContentLoaded', function () {
    trackProductView();
    init();
  });

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  PP.cart = Cart;
  window.PPTheme = PP;
})();
